import * as geojson from 'geojson';
import * as I from 'immutable';

import {
  ApiFeature,
  ApiFeatureCommonProperties,
  ApiOrderableScene,
} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {NotesActions} from 'app/stores/NotesStore';
import * as CONSTANTS from 'app/utils/constants';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import {mergeOverlaySettings} from 'app/utils/overlayUtils';

import {DEFAULT_COLORS} from './SwatchColorPicker';

export const OVERLAY_POPUP_INSET = 10;

export function getSelectedFeature(
  selectedFeatures: I.Set<I.MapAsRecord<I.ImmutableFields<ApiFeature>>>
) {
  return selectedFeatures.size === 1 ? selectedFeatures.first() : null;
}

/**
 * Helper function to use the updateFeature action to add / remove scenes from
 * the feature’s hiddenScenes "__app" property.
 *
 * Kinda tricky because it needs to modify the existing value, which is nested a
 * few layers in the properties.
 */
export function setIsImageHidden(
  updateFeature: FeaturesActions['updateFeature'],
  feature: I.MapAsRecord<I.ImmutableFields<ApiFeature>>,
  orderableScene: I.MapAsRecord<I.ImmutableFields<ApiOrderableScene>>,
  isHidden: boolean
) {
  const appProperties = feature.getIn(['properties', '__app']);

  let hiddenScenes: NonNullable<
    ApiFeatureCommonProperties[typeof CONSTANTS.APP_PROPERTIES_KEY]
  >['hiddenScenes'] = appProperties?.get('hiddenScenes')?.toJS();

  if (!hiddenScenes) {
    hiddenScenes = [];
  }

  const newAppProperties: ApiFeatureCommonProperties[typeof CONSTANTS.APP_PROPERTIES_KEY] = {
    hiddenScenes: isHidden
      ? // If isHidden, then append our scene to the end of the list
        [...hiddenScenes, [orderableScene.get('sourceId'), orderableScene.get('sensingTime')]]
      : // Otherwise, filter our scene out of the list.
        hiddenScenes.filter(
          ([sourceId, date]) =>
            orderableScene.get('sourceId') !== sourceId ||
            orderableScene.get('sensingTime') !== date
        ),
  };

  // slight slow-roll building up this value so we can establish types.
  const newProperties: Partial<ApiFeatureCommonProperties> = {
    [CONSTANTS.APP_PROPERTIES_KEY]: newAppProperties,
  };

  updateFeature(feature.get('id'), I.fromJS({properties: newProperties}));
}

/**
 * Returns the geometry the map should fit to: the selected features as a
 * FeatureCollection, falling back to the property's bounds when nothing
 * is selected. Pure — moved out of MPV's component body for readability.
 */
export function computeFocusGeometry(
  selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>,
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>
): geojson.GeoJSON | null {
  if (selectedFeatures.size) {
    return geoJsonUtils.featureCollection(selectedFeatures.toJS());
  }
  const featureCollectionBounds = selectedFeatureCollection.get('bounds');
  return featureCollectionBounds ? featureCollectionBounds.toJS() : null;
}

/**
 * Builds the overlay-settings array from a feature collection's saved Lens
 * view + the project's overlay feature collections. Used both for the lazy
 * initial visibility map and for the overlaySettings useMemo. Pure — moved
 * out of MPV's component body.
 */
export function makeOverlaySettings(
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>,
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>
): GeometryOverlaySetting[] {
  const lensView = featureCollectionUtils.getLensView(selectedFeatureCollection);
  const savedSettings: Record<string, GeometryOverlaySetting> = {};
  lensView?.get('overlays')?.forEach((o) => {
    if (o) savedSettings[o.get('name')] = o.toJS() as GeometryOverlaySetting;
  });

  const projectOverlays = featureCollectionUtils
    .uniqueFeatureCollectionNames(overlayFeatureCollections)
    .map(
      (name) =>
        overlayFeatureCollections
          .find((fc) => fc!.get('name') === name)!
          .toJS() as ApiFeatureCollection
    )
    .toArray() as ApiFeatureCollection[];

  return mergeOverlaySettings(savedSettings, projectOverlays, DEFAULT_COLORS);
}

/** The default visibility map: each overlay's name → its defaultEnabled setting. */
export function makeDefaultOverlayVisibility(
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>,
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>
): Record<string, boolean> {
  return makeOverlaySettings(selectedFeatureCollection, overlayFeatureCollections).reduce(
    (visible, {name, defaultEnabled}) => ({...visible, [name]: defaultEnabled}),
    {} as Record<string, boolean>
  );
}

function isCoordinateValid(coordinate: number, range: number) {
  return !isNaN(coordinate) && Math.abs(coordinate) <= range;
}

/**
 * Prompts the user for "lat, lng" coordinates and, if valid, registers a
 * pending point geometry on the notes store and zooms the map to it.
 */
export function promptForNoteCoordinates(
  notesActions: NotesActions,
  zoomToGeometry: ((g: geojson.GeoJSON) => void) | null
) {
  const latLng = prompt('Enter latitude, longitude.');
  if (!latLng || latLng == '') {
    return;
  }

  const [lat, lng] = latLng.split(/[ ,]+/);
  if (isCoordinateValid(parseFloat(lng), 180) && isCoordinateValid(parseFloat(lat), 90)) {
    // While we present coordinates as "latitude, longitude" in Lens for consistency
    // with common GIS tools, we reverse to "longitude, latitude" here for
    // compatibility with the geojson specification.
    const feature = geoJsonUtils.point([Number(lng), Number(lat)], {}, {id: 'point-from-coords'});
    notesActions.setPendingNoteGeometryFeature(feature);

    zoomToGeometry?.(feature);
  }
}
